import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Typography,
  Tag,
  Space,
  Divider,
  InputNumber,
  message,
  Alert,
} from "antd";

const { Title, Text } = Typography;

// Tokens autorisés pour la génération des numéros
const TOKENS = [
  "{COMPAGNIE}",
  "{POINT_VENTE}",
  "{CATEGORIE}",
  "{ORGANISME}",
  "{ANNEE2}",
  "{ANNEE4}",
  "{TYPE_SIN}",
  "{SEQ:n}",
  "{PFX}",
];

// Valeurs par défaut pour les deux types de numéros
const DEFAULTS = {
  POLICE: {
    pattern: "{COMPAGNIE}-{CATEGORIE}-{ANNEE2}-{SEQ:6}",
    sequenceLength: 6,
    anneeFormat: "ANNEE2",
    prefixe: "",
    portee: "ANNUELLE",
    actif: true,
  },
  SINISTRE: {
    pattern: "{PFX}{CATEGORIE}{ORGANISME}{TYPE_SIN}{SEQ:5}/{ANNEE2}",
    sequenceLength: 5,
    anneeFormat: "ANNEE2",
    prefixe: "S",
    portee: "ANNUELLE",
    actif: true,
  },
};

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function replaceAllLiteral(str, search, replacement) {
  return str.replace(new RegExp(escapeRegExp(search), "g"), replacement);
}

function buildNumeroPreview({ type, cfg, now = new Date(), sample = {} }) {
  const yearFull = String(now.getFullYear());
  const year2 = yearFull.slice(-2);

  const seqLenFromPattern = (() => {
    const m = (cfg.pattern || "").match(/\{SEQ:(\d+)\}/);
    if (m) return parseInt(m[1], 10);
    return cfg.sequenceLength || (type === "POLICE" ? 6 : 5);
  })();
  const seqPadded = String(sample.SEQ || 0).padStart(seqLenFromPattern, "0");

  const map = {
    "{COMPAGNIE}": sample.COMPAGNIE || "12200",
    "{POINT_VENTE}": sample.POINT_VENTE || "AG01",
    "{CATEGORIE}": sample.CATEGORIE || "M",
    "{ORGANISME}": sample.ORGANISME || "12",
    "{ANNEE2}": year2,
    "{ANNEE4}": yearFull,
    "{TYPE_SIN}": sample.TYPE_SIN || "M",
    "{PFX}": cfg.prefixe || "",
  };

  let out = cfg.pattern || "";
  out = out.replace(/\{SEQ:(\d+)\}/g, seqPadded);
  Object.entries(map).forEach(([k, v]) => {
    out = replaceAllLiteral(out, k, v ?? "");
  });
  return out;
}

function validatePattern(pattern) {
  if (!pattern) return { ok: false, errors: ["Le pattern est vide."] };
  const unknowns = Array.from(pattern.matchAll(/\{([A-Z_]+(?::\d+)?)\}/g))
    .map((m) => `{${m[1]}}`)
    .filter((t) =>
      !TOKENS.some((known) => {
        if (known === "{SEQ:n}") {
          return /^\{SEQ:\d+\}$/.test(t);
        }
        return known === t;
      })
    );
  const errors = [];
  if (unknowns.length) errors.push(`Tokens inconnus: ${unknowns.join(", ")}`);
  if ((pattern.match(/\{SEQ:/g) || []).length > 1)
    errors.push("Un seul token {SEQ:n} est autorisé.");
  return { ok: errors.length === 0, errors };
}

function NumeroForm({ type }) {
  const [cfg, setCfg] = useState(DEFAULTS[type]);
  const [example, setExample] = useState(123);
  const [preview, setPreview] = useState("");
  const [form] = Form.useForm();
  const [patternErrors, setPatternErrors] = useState([]);

  const disabled = useMemo(() => ({ TYPE_SIN: type !== "SINISTRE" }), [type]);

  useEffect(() => {
    form.setFieldsValue(cfg);
  }, []);

  useEffect(() => {
    setPreview(
      buildNumeroPreview({
        type,
        cfg,
        sample: {
          SEQ: example,
          COMPAGNIE: "12200",
          POINT_VENTE: "AG01",
          CATEGORIE: "M",
          ORGANISME: "12",
          TYPE_SIN: type === "SINISTRE" ? "M" : "",
        },
      })
    );
  }, [type, cfg, example]);

  const onValuesChange = (_, allValues) => {
    setCfg((c) => ({ ...c, ...allValues }));
    if (typeof allValues.pattern === "string") {
      const check = validatePattern(allValues.pattern);
      setPatternErrors(check.ok ? [] : check.errors);
    }
  };

  const handleSave = () => {
    if (patternErrors.length) {
      message.error(
        "Le pattern est invalide. Corrigez les erreurs avant d'enregistrer."
      );
      return;
    }
    message.success("Configuration enregistrée (simulation)");
  };

  const title =
    type === "POLICE" ? "Configuration numéro de police" : "Configuration numéro de sinistre";

  return (
    <Card bordered style={{ borderRadius: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        {title}
      </Title>
      <Form
        layout="vertical"
        form={form}
        initialValues={cfg}
        onValuesChange={onValuesChange}
      >
        <Form.Item
          label="Pattern de génération"
          name="pattern"
          rules={[{ required: true, message: "Le pattern est requis" }]}
        >
          <Input placeholder="{COMPAGNIE}-{CATEGORIE}-{ANNEE2}-{SEQ:6}" allowClear />
        </Form.Item>

        {patternErrors.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Le pattern contient des erreurs"
            description={
              <div>
                {patternErrors.map((e, i) => (
                  <div key={i}>{e}</div>
                ))}
              </div>
            }
            style={{ marginBottom: 12 }}
          />
        )}

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Longueur séquence" name="sequenceLength">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Format année" name="anneeFormat">
              <Select
                options={[
                  { value: "ANNEE2", label: "2 chiffres (ANNEE2)" },
                  { value: "ANNEE4", label: "4 chiffres (ANNEE4)" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Préfixe" name="prefixe">
              <Input placeholder={type === "SINISTRE" ? "S" : "POL"} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Portée séquence" name="portee">
              <Select
                options={[
                  { value: "ANNUELLE", label: "ANNUELLE (remise à zéro / an)" },
                  { value: "ORGANISME", label: "PAR ORGANISME" },
                  { value: "POINT_VENTE", label: "PAR POINT DE VENTE" },
                  { value: "GLOBALE", label: "GLOBALE (jamais remis à zéro)" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Actif" name="actif" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Divider style={{ margin: "8px 0 12px" }} />
        <Text strong>Tokens disponibles</Text>
        <div style={{ marginTop: 8, marginBottom: 8 }}>
          <Space wrap>
            {TOKENS.map((t) => (
              <Tag key={t} bordered>
                <code>{t}</code>
              </Tag>
            ))}
          </Space>
        </div>
        {disabled.TYPE_SIN && (
          <Text type="warning">
            Le token <code>{"{TYPE_SIN}"}</code> s'applique uniquement pour les sinistres.
          </Text>
        )}

        <Divider style={{ margin: "16px 0" }} />

        <Form.Item label="Exemple — Numéro séquentiel">
          <InputNumber
            min={0}
            value={example}
            onChange={(v) => setExample(Number(v || 0))}
          />
          <div>
            <Text type="secondary">
              Modifiez la valeur pour simuler le prochain compteur.
            </Text>
          </div>
        </Form.Item>

        <div
          style={{
            background: "#0f172a",
            color: "#fff",
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
          }}
        >
          {preview || "—"}
        </div>

        <Space>
          <Button
            type="primary"
            onClick={() =>
              setPreview(
                buildNumeroPreview({
                  type,
                  cfg,
                  sample: {
                    SEQ: example,
                    COMPAGNIE: "12200",
                    POINT_VENTE: "AG01",
                    CATEGORIE: "M",
                    ORGANISME: "12",
                    TYPE_SIN: type === "SINISTRE" ? "M" : "",
                  },
                })
              )
            }
          >
            Tester
          </Button>
          <Button onClick={handleSave}>Enregistrer</Button>
        </Space>
      </Form>
    </Card>
  );
}

export default function NumeroConfig() {
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} lg={12}>
        <NumeroForm type="POLICE" />
      </Col>
      <Col xs={24} lg={12}>
        <NumeroForm type="SINISTRE" />
      </Col>
    </Row>
  );
}